/* global Modal, trapFocus, removeTrapFocus */

/**
 * Subscription Consent Modal
 *
 * Intercepts checkout form submission (cart drawer + cart page) and shows a
 * consent modal when the cart contains subscription (selling-plan) items.
 * Consent is recorded per cart token in sessionStorage and written to cart
 * attributes so it flows through to the order.
 */

function formatMoney(cents, currency) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: currency || 'GBP',
  }).format(cents / 100);
}

if (!customElements.get('subscription-consent-modal')) {
  customElements.whenDefined('modal-dialog').then(() => {
    class SubscriptionConsentModal extends Modal {
      constructor() {
        super();

        // ---- Dataset props ----
        this.apiEndpoint = this.dataset.apiEndpoint;
        this.termsVersion = this.dataset.termsVersion;
        this.minPayments = parseInt(this.dataset.minPayments, 10) || 3;
        this.policyUrl = this.dataset.policyUrl;

        // ---- DOM refs ----
        this.acceptBtn = this.querySelector('.js-consent-accept');
        this.declineBtn = this.querySelector('.js-consent-decline');
        this.checkbox = this.querySelector('.js-consent-checkbox');
        this.errorEl = this.querySelector('.scm__error');
        this.btnText = this.querySelector('.scm__btn-text');
        this.productList = this.querySelector('[data-scm-product-list]');

        if (!this.acceptBtn || !this.declineBtn || !this.checkbox) {
          console.warn('[SubscriptionConsentModal] Required DOM elements not found');
          return;
        }

        // ---- Internal state ----
        this._pendingForm = null;
        this._bypassing = false;

        // ---- Event listeners ----
        this.checkbox.addEventListener('change', this.handleCheckboxChange.bind(this));
        this.acceptBtn.addEventListener('click', this.handleAccept.bind(this));
        this.declineBtn.addEventListener('click', this.handleDecline.bind(this));

        // ---- Attach to checkout forms ----
        this._boundHandleCheckoutSubmit = this.handleCheckoutSubmit.bind(this);
        this._attachedForms = new WeakSet();

        // Attach to any forms already in the DOM (e.g. cart page)
        this._attachToForm('#cart');
        this._attachToForm('#cart-drawer-form');

        // Watch for dynamically rendered forms (e.g. cart drawer)
        this._observer = new MutationObserver(() => {
          this._attachToForm('#cart-drawer-form');
          this._attachToForm('#cart');
        });
        this._observer.observe(document.body, { childList: true, subtree: true });

        // ---- Theme editor support ----
        if (typeof Shopify !== 'undefined' && Shopify.designMode) {
          document.addEventListener('shopify:section:select', (evt) => {
            if (evt.target === this.closest('.shopify-section')) this.open();
          });
          document.addEventListener('shopify:section:deselect', this.close.bind(this));
        }
      }

      // ------------------------------------------------------------------
      // Form attachment
      // ------------------------------------------------------------------

      /**
       * Find a form by selector and attach a submit listener if not already
       * attached.
       * @param {string} selector
       */
      _attachToForm(selector) {
        const form = document.querySelector(selector);
        if (form && !this._attachedForms.has(form)) {
          form.addEventListener('submit', this._boundHandleCheckoutSubmit);
          this._attachedForms.add(form);
        }
      }

      // ------------------------------------------------------------------
      // Checkout interception
      // ------------------------------------------------------------------

      /**
       * Intercept checkout form submission. If the cart contains subscription
       * items and the customer hasn't already consented, show the modal.
       * @param {SubmitEvent} evt
       */
      async handleCheckoutSubmit(evt) {
        // When we are re-submitting after consent, let it through.
        if (this._bypassing) return;

        // Check if consent was just given (survives page navigation).
        // This prevents a double-modal when form submit navigates to /cart
        // page instead of /checkout and the user clicks checkout again.
        const recentConsent = sessionStorage.getItem('sc-consent-bypass');
        if (recentConsent) {
          const elapsed = Date.now() - parseInt(recentConsent, 10);
          if (elapsed < 10000) {
            // Consent given within last 10 seconds — let checkout proceed
            sessionStorage.removeItem('sc-consent-bypass');
            return;
          }
          sessionStorage.removeItem('sc-consent-bypass');
        }

        // Only intercept when the submit is triggered by a checkout button.
        // Cart forms POST to /cart -- the checkout button has name="checkout".
        const submitter = evt.submitter;
        if (!submitter || submitter.getAttribute('name') !== 'checkout') return;

        evt.preventDefault();

        const form = evt.target;
        this._pendingForm = form;

        try {
          const cartResponse = await fetch('/cart.js');
          if (!cartResponse.ok) throw new Error('Failed to fetch cart');
          const cart = await cartResponse.json();

          // Filter to subscription items
          const subscriptionItems = cart.items.filter(
            (item) => item.selling_plan_allocation != null
          );

          // No subscription items -- let the NATIVE submit run.
          // Critical for BOGOS: BOGOS attaches its own submit handler that
          // swaps £0 clone variants for the real SKU before the form
          // actually posts. We must NOT route through the proxy here, or
          // that swap is bypassed and customers reach checkout with the
          // £0 clones in their order. Vanilla carts also benefit (no
          // unnecessary proxy round-trip).
          if (subscriptionItems.length === 0) {
            this._bypassing = true;
            if (typeof form.requestSubmit === 'function') {
              form.requestSubmit(submitter);
            } else {
              form.submit();
            }
            setTimeout(() => { this._bypassing = false; }, 1000);
            return;
          }

          // Always show consent for subscriptions — cart contents/value may
          // have changed since last acceptance, so we never skip the popup.

          // Populate modal and open
          this.populateCartData(subscriptionItems, cart);
          this._resetModalState();
          this.open();
        } catch (error) {
          console.error('[SubscriptionConsentModal] Error checking cart:', error);
          // On error, fall back to NATIVE submit (same reasoning as above —
          // we don't know if this is a BOGOS cart, so we must not route
          // through the proxy and risk skipping BOGOS's swap).
          this._bypassing = true;
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit(submitter);
          } else {
            form.submit();
          }
          setTimeout(() => { this._bypassing = false; }, 1000);
        }
      }

      /**
       * Re-submit the pending checkout form, bypassing our listener.
       * @param {HTMLFormElement} form
       */
      _resubmitForm(form) {
        this._bypassing = true;

        // Find the checkout button — it's associated via form="cart-drawer-form"
        // but lives OUTSIDE the form DOM (in .cart-drawer__summary), so
        // form.querySelector won't find it. Search the whole document.
        const checkoutBtn = document.querySelector('[name="checkout"][form="' + form.id + '"]')
          || form.querySelector('[name="checkout"]');

        // Route through draft order proxy if available (non-vanilla cart).
        if (window.__skqDraftOrderCheckout) {
          window.__skqDraftOrderCheckout(checkoutBtn);
          return;
        }

        // Fallback: normal form submit (vanilla cart or proxy not loaded)
        if (checkoutBtn && typeof form.requestSubmit === 'function') {
          form.requestSubmit(checkoutBtn);
        } else if (checkoutBtn) {
          checkoutBtn.click();
        } else {
          form.submit();
        }
        setTimeout(() => {
          this._bypassing = false;
        }, 1000);
      }

      // ------------------------------------------------------------------
      // Modal state
      // ------------------------------------------------------------------

      /**
       * Reset interactive elements to their default state before showing.
       */
      _resetModalState() {
        this.checkbox.checked = false;
        this.acceptBtn.disabled = true;
        this.acceptBtn.setAttribute('aria-disabled', 'true');
        this.acceptBtn.classList.remove('is-loading');
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }

      // ------------------------------------------------------------------
      // Cart data rendering
      // ------------------------------------------------------------------

      /**
       * Populate the modal product list with subscription items from the cart.
       * @param {Array} items - Subscription line items from /cart.js
       * @param {object} cart  - Full cart object from /cart.js
       */
      populateCartData(items, cart) {
        const container = this.productList;
        if (!container) return;

        const currency = cart.currency || 'GBP';
        var totalSavingsCents = 0;

        // Clear existing rows
        while (container.firstChild) {
          container.removeChild(container.firstChild);
        }

        items.forEach((item) => {
          const spa = item.selling_plan_allocation;
          const row = document.createElement('div');
          row.classList.add('scm__product-row');

          // Product title
          const titleEl = document.createElement('span');
          titleEl.classList.add('scm__product-title');
          var titleText = item.product_title;
          if (item.variant_title && item.variant_title !== 'Default Title') {
            titleText += ' \u2013 ' + item.variant_title;
          }
          if (item.quantity > 1) {
            titleText += ' x' + item.quantity;
          }
          titleEl.textContent = titleText;
          row.appendChild(titleEl);

          // Frequency / period
          const freqEl = document.createElement('span');
          freqEl.classList.add('scm__product-frequency');
          freqEl.textContent = spa.selling_plan.name;
          row.appendChild(freqEl);

          // Price per payment
          const priceEl = document.createElement('span');
          priceEl.classList.add('scm__product-price');
          priceEl.textContent = formatMoney(spa.per_delivery_price, currency) + ' per payment';
          row.appendChild(priceEl);

          // Saving per payment (compare_at_price vs per_delivery_price)
          var itemSaving = 0;
          if (spa.compare_at_price && spa.compare_at_price > spa.per_delivery_price) {
            itemSaving = spa.compare_at_price - spa.per_delivery_price;
            var savingEl = document.createElement('span');
            savingEl.classList.add('scm__product-saving');
            savingEl.textContent = 'You save ' + formatMoney(itemSaving, currency) + ' per payment';
            row.appendChild(savingEl);
            totalSavingsCents += itemSaving * item.quantity * this.minPayments;
          }

          container.appendChild(row);
        });

        // Update total minimum commitment
        const totalCents = items.reduce(
          (sum, item) => sum + item.selling_plan_allocation.per_delivery_price * item.quantity * this.minPayments,
          0
        );
        const minTotalEl = this.querySelector('[data-scm-minimum-total]');
        if (minTotalEl) {
          minTotalEl.textContent =
            formatMoney(totalCents, currency) +
            ' (' +
            this.minPayments +
            ' payments)';
        }

        // Update total savings
        const savingsRow = this.querySelector('[data-scm-total-savings]');
        const savingsValue = this.querySelector('[data-scm-total-savings-value]');
        if (savingsRow && savingsValue) {
          if (totalSavingsCents > 0) {
            savingsValue.textContent = formatMoney(totalSavingsCents, currency) + ' over ' + this.minPayments + ' payments';
            savingsRow.hidden = false;
          } else {
            savingsRow.hidden = true;
          }
        }
      }

      // ------------------------------------------------------------------
      // Open / Close overrides
      // ------------------------------------------------------------------

      /**
       * Opens the modal. Overrides base to prevent Escape from closing
       * and redirect Escape to the decline flow.
       * @param {Element} [opener]
       */
      open(opener) {
        super.open(opener);

        // Remove default Escape handler (we handle it ourselves)
        this.removeEventListener('keyup', this.keyupHandler);

        // Custom Escape handler - triggers decline flow
        this.consentKeyupHandler = (evt) => {
          if (evt.key === 'Escape') {
            evt.preventDefault();
            this.handleDecline();
          }
        };
        this.addEventListener('keyup', this.consentKeyupHandler);
      }

      /**
       * Closes the modal. Cleans up custom Escape handler.
       */
      close() {
        if (this.consentKeyupHandler) {
          this.removeEventListener('keyup', this.consentKeyupHandler);
        }
        super.close();
      }

      /**
       * Override base handleClick to prevent backdrop dismissal.
       * Only explicit button clicks are handled.
       */
      handleClick() {
        // Do nothing on backdrop click - modal requires explicit choice
      }

      // ------------------------------------------------------------------
      // Checkbox
      // ------------------------------------------------------------------

      handleCheckboxChange() {
        const checked = this.checkbox.checked;
        this.acceptBtn.disabled = !checked;
        this.acceptBtn.setAttribute('aria-disabled', String(!checked));
      }

      // ------------------------------------------------------------------
      // Accept
      // ------------------------------------------------------------------

      /**
       * Handles the accept/continue button click.
       */
      async handleAccept() {
        if (this.acceptBtn.disabled) return;

        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
        this.acceptBtn.classList.add('is-loading');
        this.acceptBtn.disabled = true;
        this.acceptBtn.setAttribute('aria-disabled', 'true');

        try {
          // Fetch cart for token and item data
          const cartResponse = await fetch('/cart.js');
          if (!cartResponse.ok) throw new Error('Failed to fetch cart');
          const cart = await cartResponse.json();

          const subscriptionItems = cart.items.filter(
            (item) => item.selling_plan_allocation != null
          );

          // Build consent payload
          const consentTimestamp = new Date().toISOString();
          const payload = {
            cartToken: cart.token,
            customerId:
              window.__st && window.__st.cid ? String(window.__st.cid) : null,
            sessionId: null,
            items: subscriptionItems.map((item) => ({
              productId: String(item.product_id),
              variantId: String(item.variant_id),
              sellingPlan: item.selling_plan_allocation.selling_plan.name,
              pricePerCycle: item.price / 100,
              quantity: item.quantity,
            })),
            minimumTotal:
              subscriptionItems.reduce(
                (sum, item) => sum + item.price * item.quantity * this.minPayments,
                0
              ) / 100,
            termsVersion: this.termsVersion,
            timestamp: consentTimestamp,
            pageUrl: window.location.href,
          };

          // Log consent to API
          var consentId = null;
          if (this.apiEndpoint) {
            const apiResponse = await fetch(this.apiEndpoint, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            });

            if (!apiResponse.ok) {
              const errorData = await apiResponse.json().catch(() => ({}));
              throw new Error(errorData.error || 'Failed to log consent');
            }

            const result = await apiResponse.json();
            consentId = result.consentId;
          }

          // Write cart attributes
          const effectiveConsentId = consentId || 'local-' + Date.now();
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              attributes: {
                subscription_consent_id: effectiveConsentId,
                subscription_terms_version: this.termsVersion,
                subscription_consent_at: consentTimestamp,
              },
            }),
          });

          // Store consent in sessionStorage (cart-level key)
          const consentKey = 'sc-consent-' + cart.token;
          sessionStorage.setItem(
            consentKey,
            JSON.stringify({
              consentId: effectiveConsentId,
              termsVersion: this.termsVersion,
              timestamp: consentTimestamp,
            })
          );

          // Close modal and re-submit the form.
          // Set a short-lived bypass flag so that if the form submit
          // navigates to /cart instead of /checkout, the new page's
          // consent modal won't fire again immediately.
          sessionStorage.setItem('sc-consent-bypass', String(Date.now()));
          this.close();

          if (this._pendingForm) {
            this._resubmitForm(this._pendingForm);
            this._pendingForm = null;
          }
        } catch (error) {
          console.error('[SubscriptionConsentModal]', error);
          this.errorEl.textContent = 'Something went wrong. Please try again.';
          this.errorEl.hidden = false;
          this.acceptBtn.disabled = false;
          this.acceptBtn.setAttribute('aria-disabled', 'false');
          this.acceptBtn.classList.remove('is-loading');
        }
      }

      // ------------------------------------------------------------------
      // Decline
      // ------------------------------------------------------------------

      /**
       * Handles the decline action. Closes the modal WITHOUT mutating the
       * cart — the user's subscription items remain selected exactly as they
       * were. From the user's point of view, declining is a no-op: they're
       * returned to the cart and can either remove subscription items
       * manually (via the line-item × in the drawer) or click checkout again
       * to re-review consent.
       *
       * This avoids the previous UX conflict where we stripped the
       * selling_plan in the cart session but RecurPay's widget kept its
       * internal "subscription selected" state, producing a confusing
       * mismatch between the widget UI and the underlying cart.
       */
      handleDecline() {
        this.close();
        this._pendingForm = null;
      }

      // ------------------------------------------------------------------
      // Cleanup
      // ------------------------------------------------------------------

      disconnectedCallback() {
        if (this._observer) {
          this._observer.disconnect();
        }
      }
    }

    customElements.define('subscription-consent-modal', SubscriptionConsentModal);
  });
}
